import { Fragment, type ComponentType, type ReactNode, type CSSProperties } from 'react';

import type {
  LayoutOverrides,
  TemplateSlotList,
  ConditionExpression,
  CatalogueTemplateRegion,
} from '../schema';
import type {
  CardBlockProps,
  CardBlockRegistry,
  HeaderBlockProps,
  HeaderBlockRegistry,
  PanelBlockProps,
  PanelBlockRegistry,
  BlockSlotAccessors,
} from './types';

type BlockRegion = 'card' | 'panel' | 'header';

export type GenericBlockProps = CardBlockProps | PanelBlockProps | HeaderBlockProps;

type GenericBlockRegistry =
  | CardBlockRegistry
  | PanelBlockRegistry
  | HeaderBlockRegistry;

export interface DeclarativeRenderConfig {
  slots?: TemplateSlotList;
  layout?: LayoutOverrides;
  regions?: Partial<Record<CatalogueTemplateRegion, TemplateSlotList>>;
  layoutByRegion?: Partial<Record<CatalogueTemplateRegion, LayoutOverrides>>;
}

export type DeclarativeComponent<Props extends GenericBlockProps> = ComponentType<Props> & {
  renderBlocks?: DeclarativeRenderConfig | null;
};

export type SlotRenderer<Props extends GenericBlockProps> = (props: Props) => ReactNode;

interface CreateSlotRendererOptions {
  region: BlockRegion;
  slots?: TemplateSlotList;
  registry: GenericBlockRegistry;
}

interface ConditionContext {
  [key: string]: unknown;
}

const wrapNodes = (nodes: ReactNode[]): ReactNode => {
  if (nodes.length === 0) {
    return null;
  }

  if (nodes.length === 1) {
    return nodes[0] ?? null;
  }

  return <Fragment>{nodes}</Fragment>;
};

const getContextValue = (context: ConditionContext, path: string): unknown => {
  const segments = path.split('.').filter(Boolean);
  let current: unknown = context;

  for (const segment of segments) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }

    current = (current as Record<string, unknown>)[segment];
  }

  return current;
};

const parseLiteral = (raw: string): unknown => {
  const trimmed = raw.trim();

  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith('\'') && trimmed.endsWith('\''))) {
    return trimmed.slice(1, -1);
  }

  if (/^true$/i.test(trimmed)) {
    return true;
  }

  if (/^false$/i.test(trimmed)) {
    return false;
  }

  if (trimmed === 'null') {
    return null;
  }

  if (trimmed === 'undefined') {
    return undefined;
  }

  const asNumber = Number(trimmed);
  if (!Number.isNaN(asNumber)) {
    return asNumber;
  }

  return trimmed;
};

const evaluateStringCondition = (expression: string, context: ConditionContext): boolean => {
  let expr = expression.trim();

  if (!expr) {
    return true;
  }

  let negate = false;
  while (expr.startsWith('!')) {
    negate = !negate;
    expr = expr.slice(1).trim();
  }

  const equalityMatch = expr.match(/^([^!=]+)(==|!=)(.+)$/);
  let result: boolean;

  if (equalityMatch) {
    const [, left, operator, rightRaw] = equalityMatch;
    const leftValue = getContextValue(context, left.trim());
    const rightValue = parseLiteral(rightRaw);

    result = operator === '==' ? leftValue === rightValue : leftValue !== rightValue;
  } else {
    const value = getContextValue(context, expr);
    result = Boolean(value);
  }

  return negate ? !result : result;
};

const evaluateCondition = (condition: ConditionExpression | undefined, context: ConditionContext): boolean => {
  if (!condition) {
    return true;
  }

  if (typeof condition === 'string') {
    return evaluateStringCondition(condition, context);
  }

  if (condition.all) {
    return condition.all.every((child) => evaluateCondition(child, context));
  }

  if (condition.any) {
    return condition.any.some((child) => evaluateCondition(child, context));
  }

  if (condition.not) {
    return !evaluateCondition(condition.not, context);
  }

  return true;
};

const hasDiscount = (item: CardBlockProps['item']): boolean => {
  if (!item) {
    return false;
  }

  if (Array.isArray(item.promotions) && item.promotions.length > 0) {
    return true;
  }

  if (typeof item.discount === 'number') {
    return item.discount > 0;
  }

  if (item.price && typeof (item.price as { discount_percent?: number }).discount_percent === 'number') {
    return ((item.price as { discount_percent?: number }).discount_percent ?? 0) > 0;
  }

  return false;
};

const createCardContext = (props: CardBlockProps): ConditionContext => {
  const priceValue = props.item?.price && typeof (props.item.price as { value?: number }).value === 'number'
    ? (props.item.price as { value: number }).value
    : undefined;
  const hasVariantPrice = Array.isArray(props.item?.children)
    && props.item.children.some((child) => {
      const childPrice = child.price && typeof (child.price as { value?: number }).value === 'number'
        ? (child.price as { value: number }).value
        : undefined;
      return (childPrice ?? 0) > 0;
    });
  const hasPrice = Boolean((props.item?.price && (priceValue ?? 0) > 0) || hasVariantPrice);

  return {
    ...props,
    props,
    hasPrice,
    hasDiscount: hasDiscount(props.item),
    isInCart: Boolean(props.cartState?.isInCart),
    cartQuantity: props.cartState?.quantity ?? 0,
    cartIsUpdating: Boolean(props.cartState?.isUpdating),
    hasHighlights:
      Boolean(props.highlightState?.label) ||
      Boolean(props.highlightState?.isNew) ||
      Boolean(props.highlightState?.isSoldOut),
    hasExtraBadges: Boolean(props.extraBadges?.length),
  };
};

const createPanelContext = (props: PanelBlockProps): ConditionContext => {
  const hasResults = Boolean(props.status?.hasResults);
  const isLoading = Boolean(props.status?.isLoading);
  const hasMoreResults = Boolean(props.pagination?.hasMore);
  const hasFilters = Boolean(props.filters?.chips?.length);
  const hasSort = Boolean(props.sort?.options?.length);
  const hasEmptyState = Boolean(props.emptyState);
  const hasBanners = Boolean(props.banners?.length);

  return {
    ...props,
    props,
    hasResults,
    isLoading,
    hasMoreResults,
    hasFilters,
    hasSort,
    hasEmptyState,
    hasBanners,
    hasItems: Array.isArray(props.items) && props.items.length > 0,
  };
};

const createHeaderContext = (props: HeaderBlockProps): ConditionContext => {
  const slots = props.slots ?? {};
  const hasNavigation = Boolean(props.navigationLinks?.length || slots.navigation);
  const hasActions = Boolean(slots.actions);
  const hasConfigurator = Boolean(slots.configurator);

  return {
    ...props,
    props,
    hasNavigation,
    hasActions,
    hasConfigurator,
  };
};

const createContext = (region: BlockRegion, props: GenericBlockProps): ConditionContext => {
  switch (region) {
    case 'card':
      return createCardContext(props as CardBlockProps);
    case 'panel':
      return createPanelContext(props as PanelBlockProps);
    case 'header':
      return createHeaderContext(props as HeaderBlockProps);
    default:
      return { ...props, props };
  }
};

const renderSlotList = <Props extends GenericBlockProps>(
  region: BlockRegion,
  slotList: TemplateSlotList,
  props: Props,
  registry: GenericBlockRegistry,
): ReactNode[] => {
  const elements: ReactNode[] = [];

  slotList.forEach((entry, index) => {
    const registrySection = registry as unknown as Record<string, ComponentType<GenericBlockProps>>;
    const BlockComponent = registrySection[entry.block] as ComponentType<Props> | undefined;

    if (!BlockComponent) {
      return;
    }

    const mergedProps = {
      ...(props as unknown as Record<string, unknown>),
      ...(entry.props as Record<string, unknown> | undefined),
    } as unknown as Props;

    const context = createContext(region, mergedProps as GenericBlockProps);
    if (!evaluateCondition(entry.when, context)) {
      return;
    }

    const blockProps = {
      ...(mergedProps as unknown as Record<string, unknown>),
    } as unknown as Props & BlockSlotAccessors;

    if (entry.slots) {
      const childBaseProps = {
        ...(mergedProps as unknown as Record<string, unknown>),
      } as unknown as Props;
      delete (childBaseProps as unknown as BlockSlotAccessors).renderSlot;
      delete (childBaseProps as unknown as BlockSlotAccessors).hasSlot;

      const renderSlot = (slotName: string): ReactNode => {
        const childList = entry.slots?.[slotName];
        if (!childList?.length) {
          return null;
        }

        const childNodes = renderSlotList(region, childList, childBaseProps, registry);
        return wrapNodes(childNodes);
      };

      const hasSlot = (slotName: string): boolean => Boolean(entry.slots?.[slotName]?.length);

      const staticAccessors = BlockComponent as ComponentType<Props> & BlockSlotAccessors;

      if (!blockProps.renderSlot) {
        blockProps.renderSlot = staticAccessors.renderSlot ?? renderSlot;
      }
      if (!blockProps.hasSlot) {
        blockProps.hasSlot = staticAccessors.hasSlot ?? hasSlot;
      }
    }

    const key = entry.key ?? `${entry.block}-${index}`;
    elements.push(<BlockComponent key={key} {...(blockProps as Props)} />);
  });

  return elements;
};

export const createSlotRenderer = <Props extends GenericBlockProps>({
  region,
  slots,
  registry,
}: CreateSlotRendererOptions): SlotRenderer<Props> | null => {
  if (!slots || slots.length === 0) {
    return null;
  }

  return (props: Props) => wrapNodes(renderSlotList(region, slots, props, registry));
};

export const isDeclarativeComponent = <Props extends GenericBlockProps>(
  component: ComponentType<Props>,
): component is ComponentType<Props> & DeclarativeComponent<Props> =>
  Boolean((component as DeclarativeComponent<Props>).renderBlocks);

export interface LayoutRenderProps {
  className?: string;
  style?: CSSProperties;
  fullWidth?: boolean;
  ratio?: string;
}

const parseAspectRatio = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const [width, height] = value.split(':');
  const widthValue = Number(width);
  const heightValue = Number(height);

  if (!Number.isFinite(widthValue) || !Number.isFinite(heightValue) || heightValue === 0) {
    return null;
  }

  return `${widthValue} / ${heightValue}`;
};

export const resolveLayoutProps = (layout?: LayoutOverrides | null): LayoutRenderProps => {
  if (!layout) {
    return {};
  }

  const resolvedStyle: CSSProperties = { ...(layout.style ?? {}) } as CSSProperties;

  if (layout.tokens) {
    Object.entries(layout.tokens).forEach(([token, value]) => {
      const cssVar = token.startsWith('--') ? token : `--${token}`;
      (resolvedStyle as Record<string, string | number>)[cssVar] = value;
    });
  }

  const aspectRatio = parseAspectRatio(layout.ratio);
  if (aspectRatio) {
    resolvedStyle.aspectRatio = aspectRatio;
  }

  return {
    className: layout.className,
    style: Object.keys(resolvedStyle).length > 0 ? resolvedStyle : undefined,
    fullWidth: layout.fullWidth,
    ratio: layout.ratio,
  };
};

export const CARD_REGION: BlockRegion = 'card';
export const PANEL_REGION: BlockRegion = 'panel';
export const HEADER_REGION: BlockRegion = 'header';
